import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
  // PayloadAction,
  // createAction,
} from "@reduxjs/toolkit";
import * as Sentry from "@sentry/nextjs";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { CompanyAdminUser } from "admin/types/companyAdminUser";
import { AdminUserRoleNumber } from "../constants/adminUserRoles";
import { CompanyAdminUserRoles } from "admin/constants/companyAdminUserRoles";
import { RootState } from "admin/store";

const key = "currentCompanyAdminUser";
const adapter = createEntityAdapter<CompanyAdminUser.CompanyAdminUser>();

const initialStateValues: CompanyAdminUser.CompanyAdminUserState = {
  companyAdminUser: null,
  errors: null,
  loading: false,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async * */
export const authCompanyAdminUser = createAsyncThunk<
  {
    companyAdminUser: CompanyAdminUser.CompanyAdminUser;
  },
  {
    headers?: any;
    isServerSide?: boolean;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/authCompanyAdminUser`, async (args, { rejectWithValue }) => {
  try {
    const { headers, isServerSide = false, locale } = args;
    // console.log(headers);
    const result = await createWrapper({ isServerSide, locale }).get(
      `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/current_company_admin_user.json`,
      { headers },
    );
    return {
      companyAdminUser: result.data.companyAdminUser,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

interface SignInParams {
  email: string;
  password: string;
}

interface InvitationAcceptanceParams {
  mail_authentication_token: string;
  password: string;
  password_confirmation: string;
}

export const signInCompanyAdminUser = createAsyncThunk<
  {
    companyAdminUser: CompanyAdminUser.CompanyAdminUser;
  },
  { params: SignInParams },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/signInCompanyAdminUser`, async (args, { rejectWithValue }) => {
  try {
    const { params } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/company_admin_user_auth/sign_in`,
      {
        ...params,
      },
    );
    return {
      companyAdminUser: result.data.companyAdminUser,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const signOutCompanyAdminUser = createAsyncThunk<
  void,
  void,
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/signOutCompanyAdminUser`, async (_, { rejectWithValue }) => {
  try {
    await createWrapper().delete(
      `${API_BASE_PATH}/company_admin_user_auth/sign_out`,
    );
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postCompanyAdminUserInvitationAcceptance = createAsyncThunk<
  void,
  { params: InvitationAcceptanceParams },
  {
    rejectValue: ErrorResponse;
  }
>(
  `${key}/postCompanyAdminUserInvitationAcceptance`,
  async (args, { rejectWithValue }) => {
    try {
      const { params } = args;
      await createWrapper().post(
        `${API_BASE_PATH}/company_admin_users/invitation_acceptance`,
        {
          ...params,
        },
      );
      return;
    } catch (err) {
      return rejectWithValue(err.data.errors);
    }
  },
);

/** slice **/
export const currentCompanyAdminUserSlice = createSlice({
  name: key,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        if (action.payload.currentCompanyAdminUser?.companyAdminUser) {
          Sentry.configureScope((scope) => {
            scope.setUser({
              id: `${action.payload.currentCompanyAdminUser.companyAdminUser.modelNameAndId}`,
              companyId: `${action.payload.currentCompanyAdminUser.companyAdminUser.companyId}`,
            });
          });
        }
        return { ...state, ...action.payload.currentCompanyAdminUser };
      })
      .addCase(authCompanyAdminUser.fulfilled, (state, action) => {
        state.companyAdminUser = action.payload.companyAdminUser;
      })
      .addCase(signInCompanyAdminUser.fulfilled, (state, action) => {
        state.companyAdminUser = action.payload.companyAdminUser;
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector * */
const stateSelector = (state: RootState) => state[key];

export const currentCompanyAdminUserSelector = createSelector(
  stateSelector,
  (state) => state.companyAdminUser,
);

export const errorSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const canShowAdminUser = (
  adminUser?: CompanyAdminUser.CompanyAdminUser,
) => {
  return adminUser
    ? AdminUserRoleNumber[adminUser.role] <= AdminUserRoleNumber.admin
    : false;
};

export const canShowCompany = (
  adminUser?: CompanyAdminUser.CompanyAdminUser,
) => {
  return adminUser
    ? AdminUserRoleNumber[adminUser.role] <= AdminUserRoleNumber.admin
    : false;
};

export const redirectPropsIfNotAuthorize = (
  adminUserResult: {
    companyAdminUser?: CompanyAdminUser.CompanyAdminUser | null;
    errorCode?: string;
  },
  resources: string[],
  hotelId?: string | null,
) => {
  if (!adminUserResult) {
    return {
      redirect: {
        destination: `/admin/signin`,
        permanent: false,
      },
    };
  }
  if (adminUserResult.errorCode === "exception") {
    return {
      redirect: {
        destination: `/admin/signin`,
        permanent: false,
      },
    };
  }
  const adminUser = adminUserResult.companyAdminUser;
  if (!adminUser) {
    return {
      redirect: {
        destination: `/admin/signin`,
        permanent: false,
      },
    };
  }

  let authorized = adminUser.abilities;
  let redirectProps = null;
  resources.some((resource: string) => {
    authorized = authorized[resource];
    if (!authorized) {
      redirectProps = {
        redirect: {
          destination: `/admin/errors/access_denied`,
          permanent: false,
        },
      };
      return redirectProps;
    }
  });
  if (redirectProps) {
    return redirectProps;
  }
  if (
    adminUser.role === CompanyAdminUserRoles.facilityAdmin ||
    adminUser.role === CompanyAdminUserRoles.facilityOperator
  ) {
    if (hotelId && !adminUser.hotelCodes.includes(hotelId)) {
      return {
        redirect: {
          destination: `/admin/errors/access_denied`,
          permanent: false,
        },
      };
    }
  }
};

export const redirectPathIfNotAuthorize = (
  adminUserResult: {
    companyAdminUser?: CompanyAdminUser.CompanyAdminUser | null;
    errorCode?: string;
  },
  resources: string[],
  hotelId?: string | null,
) => {
  if (!adminUserResult) {
    return `/admin/signin`;
  }
  if (adminUserResult.errorCode === "exception") {
    return `/admin/signin`;
  }
  const adminUser = adminUserResult.companyAdminUser;
  if (!adminUser) {
    return `/admin/signin`;
  }

  let authorized = adminUser.abilities;
  let redirectPath = null;
  resources.some((resource: string) => {
    authorized = authorized[resource];
    if (!authorized) {
      redirectPath = `/admin/errors/access_denied`;
      return redirectPath;
    }
  });
  if (redirectPath) {
    return redirectPath;
  }
  if (
    adminUser.role === CompanyAdminUserRoles.facilityAdmin ||
    adminUser.role === CompanyAdminUserRoles.facilityOperator
  ) {
    if (hotelId && !adminUser.hotelCodes.includes(hotelId)) {
      return `/admin/errors/access_denied`;
    }
  }
};

export const getRedirectToHotelValue = (hotelCode: string) => {
  return {
    redirect: {
      destination: `/admin/hotels/${hotelCode}`,
      permanent: false,
    },
  };
};

export const redirectHotelPath = (hotelCode: string) => {
  return `/admin/hotels/${hotelCode}`;
};
